import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download } from 'lucide-react'
import toast from 'react-hot-toast'
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import { mensajeDeError } from '@/lib/errores'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import Proyecto from './Proyecto'
import { SEVERIDAD } from './estados'

/**
 * Asistente de MIGRACIÓN del asesor: traer el catálogo y la cartera del sistema anterior.
 *
 * Aquí solo vive el marco de la página. El trabajo está en `Proyecto` — recopilar, preparar,
 * cuadrar y cargar.
 *
 * Antes esta pantalla tenía DOS asistentes completos, uno encima del otro. El segundo (Origen
 * → Mapeo → Validación → Carga, para un archivo suelto) estaba plegado dentro de un `<details>`
 * cerrado, y resultaba ser el único sitio con perfiles de mapeo, CSV de rechazos y deshacer la
 * carga entera. O sea que las tres cosas que más falta hacen vivían detrás de un acordeón que
 * nadie sabía que existía, mientras el asistente visible no las tenía. Se retiró y esas
 * capacidades subieron a la mesa de trabajo, donde se usan.
 *
 * El caso que justificaba el asistente rápido —«el cliente me mandó cuatro filas por
 * WhatsApp»— sigue cubierto: se pegan en el paso Recopilar y entran por la misma puerta.
 */
export default function ConsultorMigracion() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [informe, setInforme] = useState(null)
  const [auditando, setAuditando] = useState(false)

  const auditar = async () => {
    setAuditando(true)
    try {
      const r = await cd2bPropuestaService.auditoriaCalidad(id)
      setInforme(r?.data || null)
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo revisar la calidad de los datos'))
    } finally {
      setAuditando(false)
    }
  }

  const exportar = async () => {
    try {
      const r = await cd2bPropuestaService.exportarDatosCliente(id)
      const blob = new Blob([JSON.stringify(r?.data ?? {}, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `datos-cliente-${id}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Exportado. Es un volcado lógico, no un respaldo de la base.')
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo exportar'))
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Migrar datos del cliente"
        description="Trae el catálogo y la cartera del sistema anterior. Lo cargado se puede deshacer entero."
        actions={
          <div className="flex flex-wrap gap-2">
            {/*
              «Llevarse los datos» es a donde apunta el menú de la cotización. Estaba dentro de
              un `<details open={paso === 3}>` cuyo `paso` era el del OTRO asistente, así que
              nunca se abría: el menú llevaba a una acción invisible.
            */}
            <Button variant="ghost" onClick={exportar}>
              <Download size={16} className="mr-1.5" /> Llevarse los datos
            </Button>
            <Button variant="ghost" onClick={() => navigate('/consultor/cotizaciones')}>
              <ArrowLeft size={16} className="mr-1.5" /> Volver
            </Button>
          </div>
        }
      />

      <Proyecto propuestaId={id} />

      {/*
        La calidad de los datos es un informe DE DESPUÉS: mirarlo antes de cargar nada no dice
        nada útil. Por eso va al pie y se pide a mano — pero visible, no plegado.
      */}
      <Card className="mt-8">
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-body-sm font-medium text-neutral-900 dark:text-neutral-50">
              Calidad de los datos
            </span>
            <Button variant="secondary" size="xs" onClick={auditar} disabled={auditando}>
              {auditando ? 'Revisando…' : 'Revisar ahora'}
            </Button>
            <span className="text-caption text-neutral-500 dark:text-neutral-400">
              Qué le falta a los datos ya cargados para que el cliente pueda operar.
            </span>
          </div>

          {informe && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-4 text-body-sm text-neutral-600 dark:text-neutral-400">
                <span>{informe.totales?.productos ?? 0} productos</span>
                <span>{informe.totales?.clientes ?? 0} clientes</span>
                <span>{informe.totales?.proveedores ?? 0} proveedores</span>
                <span>{informe.totales?.cuentas ?? 0} cuentas</span>
              </div>
              {informe.hallazgos?.length === 0 ? (
                <p className="text-body-sm text-success-700 dark:text-success-300">
                  Sin hallazgos: los datos están listos para operar.
                </p>
              ) : (
                <ul className="space-y-2">
                  {(informe.hallazgos || []).map((h) => {
                    const sev = SEVERIDAD[h.severidad] || SEVERIDAD.aviso
                    return (
                      <li key={h.clave} className="text-body-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant={sev.variante}>{sev.etiqueta}</Badge>
                          <span className="font-medium text-neutral-900 dark:text-neutral-50">{h.titulo}</span>
                          <span className="tabular-nums text-neutral-500 dark:text-neutral-400">({h.cuantos})</span>
                        </div>
                        <p className="mt-0.5 text-neutral-600 dark:text-neutral-400">{h.consecuencia}</p>
                        <p className="mt-0.5 text-caption text-neutral-500 dark:text-neutral-400">
                          {(h.ejemplos || []).slice(0, 5).join(' · ')}
                        </p>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
